import React, { useContext, useEffect, useState } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, Search, Plus, User, FileText, Trash2, 
  CheckCircle2, X, Edit2, ChevronLeft, ChevronRight,
  ClipboardList, Users, MapPin, Clock, DollarSign,
  Calendar, MessageSquare, BadgeCheck, AlertCircle,
  MoreVertical, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VacantesProPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [vacantes, setVacantes] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isVacanteSidebarOpen, setIsVacanteSidebarOpen] = useState(false);
  const [isPostulacionesOpen, setIsPostulacionesOpen] = useState(false);
  const [selectedVacante, setSelectedVacante] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  
  // Modal Agendar state
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [schedulingData, setSchedulingData] = useState({
    fecha: '',
    hora: '10:00'
  });
  
  // Form State for Vacancy
  const [vacanteForm, setVacanteForm] = useState({
    puesto: '',
    descripcion: '',
    requisitos: '',
    ubicacion: '',
    salario_rango: '',
    horario: '',
    estado: 'abierta',
    plazas_disponibles: 1,
    responsable: ''
  });

  useEffect(() => {
    fetchData();
  }, [tenantSlug]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/vacantes`),
        axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones`)
      ]);
      setVacantes(vRes.data || []);
      setPostulaciones(pRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar datos. Verifique los silos "vacantes" y "postulaciones"');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddVacante = () => {
    setIsEditing(false);
    setEditingRecordId(null);
    setVacanteForm({
      puesto: '',
      descripcion: '',
      requisitos: '',
      ubicacion: '',
      salario_rango: '',
      horario: '',
      estado: 'abierta',
      plazas_disponibles: 1,
      responsable: ''
    });
    setIsVacanteSidebarOpen(true);
  };

  const handleEditVacante = (vacante) => {
    setIsEditing(true);
    setEditingRecordId(vacante._id);
    setVacanteForm({
      puesto: vacante.puesto || '',
      descripcion: vacante.descripcion || '',
      requisitos: vacante.requisitos || '',
      ubicacion: vacante.ubicacion || '',
      salario_rango: vacante.salario_rango || '',
      horario: vacante.horario || '',
      estado: vacante.estado || 'abierta',
      plazas_disponibles: vacante.plazas_disponibles || 1,
      responsable: vacante.responsable || ''
    });
    setIsVacanteSidebarOpen(true);
  };

  const handleSaveVacante = async () => {
    if (!vacanteForm.puesto) {
      toast.error("El nombre del puesto es obligatorio");
      return;
    }
    
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/vacantes/${editingRecordId}`, vacanteForm);
        toast.success("Vacante actualizada");
      } else {
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/vacantes/inject`, { data: { ...vacanteForm, fecha_publicacion: new Date().toISOString() } });
        toast.success("Vacante publicada");
      }
      setIsVacanteSidebarOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Error al guardar vacante");
    }
  };

  const handleDeleteVacante = async (id) => {
    if(!window.confirm("¿Seguro que deseas eliminar esta vacante?")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/vacantes/${id}`);
      toast.success("Vacante eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar vacante");
    }
  };

  const handleViewPostulaciones = (vacante) => {
    setSelectedVacante(vacante);
    setIsPostulacionesOpen(true);
  };

  const handleUpdatePostulacionStatus = async (postId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones/${postId}`, { estado: newStatus });
      toast.success(`Estado actualizado a ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleAgendarEntrevista = (postulacion) => {
    setSelectedCandidate(postulacion);
    setSchedulingData({
      fecha: new Date().toISOString().split('T')[0],
      hora: '10:00'
    });
    setIsSchedulingOpen(true);
  };

  const confirmScheduling = async () => {
    if (!schedulingData.fecha || !schedulingData.hora || !selectedCandidate) {
      toast.error("Por favor selecciona fecha y hora");
      return;
    }

    try {
      const combinedDateTime = `${schedulingData.fecha}T${schedulingData.hora}:00`;
      const citaPayload = {
        data: {
          folio: `ENT-${Math.floor(1000 + Math.random() * 9000)}`,
          cliente: `${selectedCandidate.nombre} (Postulación: ${selectedCandidate.vacante_ref})`,
          fecha_hora: combinedDateTime,
          servicio: "Entrevista de Reclutamiento",
          estado: "Programada",
          notas: `Vacante: ${selectedCandidate.vacante_ref}`,
          fecha_registro: new Date().toISOString()
        }
      };

      await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/citas/inject`, citaPayload);
      
      await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones/${selectedCandidate._id}`, { 
        estado: 'entrevista',
        cita_entrevista_ref: citaPayload.data.folio
      });

      toast.success("Entrevista agendada correctamente");
      fetchData();
      setIsSchedulingOpen(false);
    } catch (err) {
      toast.error("Error al agendar entrevista. Verifique el silo 'citas'");
    }
  };

  const filteredVacantes = vacantes.filter(v => 
    v.puesto?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    v.ubicacion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    abiertas: vacantes.filter(v => v.estado === 'abierta').length,
    nuevas: postulaciones.filter(p => p.estado === 'nueva').length,
    entrevistas: postulaciones.filter(p => p.estado === 'entrevista').length
  };

  return (
    <div className="h-full flex flex-col pt-8 lg:pt-0">
      {/* Premium Cyan Header */}
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-cyan-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-cyan-400" />
                <span className="text-[9px] uppercase tracking-[0.3em] font-black text-cyan-400">Talent Engine</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Vacantes</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">Gestión de capital humano y reclutamiento inteligente.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[500px]"
            >
                <div className="flex items-center bg-slate-900/50 rounded-xl border border-white/5 px-2">
                  <button className="p-2 text-slate-600 hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                  <div className="w-[1px] h-4 bg-white/5 mx-1" />
                  <button className="p-2 text-slate-600 hover:text-white transition-colors"><ChevronRight className="w-4 h-4" /></button>
                </div>

                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar vacante..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                  
                <button 
                  onClick={handleOpenAddVacante}
                  className="px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nueva</span>
                </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 group hover:border-cyan-500/30 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
            <Briefcase className="w-8 h-8 text-cyan-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{stats.abiertas}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Vacantes Abiertas</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 group hover:border-blue-500/30 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
            <Users className="w-8 h-8 text-blue-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{stats.nuevas}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Postulaciones Nuevas</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 group hover:border-indigo-500/30 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
            <Calendar className="w-8 h-8 text-indigo-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{stats.entrevistas}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Entrevistas Agendadas</p>
          </div>
        </div>
      </div>

      {/* Grid of Vacancies */}
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        {loading ? (
           <div className="p-12 text-center text-slate-500 font-medium">Escaneando base de talentos...</div>
        ) : filteredVacantes.length === 0 ? (
           <div className="p-20 text-center bg-slate-900/50 border border-white/5 rounded-[2rem]">
              <Briefcase className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-lg text-slate-400 font-medium">No hay vacantes registradas</p>
           </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 pb-10">
            {filteredVacantes.map((vacante, index) => {
              const vPostulaciones = postulaciones.filter(p => p.vacante_ref === vacante.puesto);
              const nuevasCount = vPostulaciones.filter(p => p.estado === 'nueva').length;
              
              return (
                <motion.div 
                  key={vacante._id || index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-slate-900 border border-white/5 rounded-3xl p-5 hover:border-cyan-500/30 transition-all group relative overflow-hidden max-w-sm"
                >
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-lg">
                        <Briefcase className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-md font-bold text-white tracking-tight leading-none mb-1">{vacante.puesto}</h3>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-slate-500" />
                          <span className="text-[9px] text-slate-500 uppercase font-black tracking-widest">{vacante.ubicacion || 'Remoto'}</span>
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border ${
                      vacante.estado === 'abierta' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                    }`}>
                      {vacante.estado || 'abierta'}
                    </span>
                  </div>

                  <div className="flex items-center gap-6 mb-5 px-1">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-3.5 h-3.5 text-cyan-400" />
                      <span className="text-[11px] text-slate-300 font-bold">{vacante.salario_rango || 'No definido'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-[11px] text-slate-300 font-bold">{vacante.horario || 'No definido'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <div className="flex items-center gap-2">
                      <div className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest ${nuevasCount > 0 ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-slate-800 text-slate-500'}`}>
                        {nuevasCount} Nuevas
                      </div>
                      <span className="text-[9px] text-slate-500 font-black uppercase tracking-widest">{vPostulaciones.length} Total</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => handleEditVacante(vacante)}
                        className="p-2 text-slate-500 hover:text-white transition-colors hover:bg-white/5 rounded-lg"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => handleViewPostulaciones(vacante)}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white text-[9px] font-black uppercase tracking-[0.1em] rounded-lg transition-all flex items-center gap-1.5"
                      >
                        <Eye className="w-3 h-3" />
                        <span>Ver</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Vacancy Form Sidebar */}
      <AnimatePresence>
        {isVacanteSidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsVacanteSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              className="fixed top-0 right-0 h-full w-full max-w-[500px] bg-[#0F172A] border-l border-white/10 z-[101] flex flex-col"
            >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                    <Briefcase className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">{isEditing ? 'Editar Vacante' : 'Nueva Vacante'}</h2>
                    <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest">Publicación de Puesto</p>
                  </div>
                </div>
                <button onClick={() => setIsVacanteSidebarOpen(false)} className="p-2 hover:bg-white/5 rounded-lg text-slate-500">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Puesto *</label>
                    <input 
                      type="text" 
                      className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm"
                      value={vacanteForm.puesto}
                      onChange={e => setVacanteForm({...vacanteForm, puesto: e.target.value})}
                      placeholder="Ej: Operador de Producción"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Ubicación</label>
                      <input 
                        type="text" 
                        className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm"
                        value={vacanteForm.ubicacion}
                        onChange={e => setVacanteForm({...vacanteForm, ubicacion: e.target.value})}
                        placeholder="Planta Norte"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Plazas</label>
                      <input 
                        type="number" 
                        className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm"
                        value={vacanteForm.plazas_disponibles}
                        onChange={e => setVacanteForm({...vacanteForm, plazas_disponibles: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Descripción</label>
                    <textarea 
                      rows="3"
                      className="w-full bg-black/40 border border-white/5 rounded-xl p-4 text-white font-medium outline-none focus:border-cyan-500/50 transition-all text-sm resize-none"
                      value={vacanteForm.descripcion}
                      onChange={e => setVacanteForm({...vacanteForm, descripcion: e.target.value})}
                      placeholder="Descripción de tareas y responsabilidades..."
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Requisitos</label>
                    <textarea 
                      rows="3"
                      className="w-full bg-black/40 border border-white/5 rounded-xl p-4 text-white font-medium outline-none focus:border-cyan-500/50 transition-all text-sm resize-none"
                      value={vacanteForm.requisitos}
                      onChange={e => setVacanteForm({...vacanteForm, requisitos: e.target.value})}
                      placeholder="Experiencia, estudios, habilidades..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Salario</label>
                      <input 
                        type="text" 
                        className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm"
                        value={vacanteForm.salario_rango}
                        onChange={e => setVacanteForm({...vacanteForm, salario_rango: e.target.value})}
                        placeholder="$8,000 - $10,000"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Horario</label>
                      <select 
                        className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm appearance-none mb-2"
                        value={[
                          "Lun-Vie (8am - 6pm)",
                          "Lun-Sáb (8am - 2pm)",
                          "Turno Rotativo",
                          "Turno Nocturno",
                          "Medio Tiempo",
                          "Fines de Semana",
                          "Por Definir",
                          ""
                        ].includes(vacanteForm.horario) ? vacanteForm.horario : "custom"}
                        onChange={e => {
                          if (e.target.value === "custom") {
                            setVacanteForm({...vacanteForm, horario: 'Lunes a Viernes '}); // Default starting text for custom
                          } else {
                            setVacanteForm({...vacanteForm, horario: e.target.value});
                          }
                        }}
                      >
                        <option value="">Seleccionar Horario</option>
                        <option value="Lun-Vie (8am - 6pm)">Lun-Vie (8am - 6pm)</option>
                        <option value="Lun-Sáb (8am - 2pm)">Lun-Sáb (8am - 2pm)</option>
                        <option value="Turno Rotativo">Turno Rotativo</option>
                        <option value="Turno Nocturno">Turno Nocturno</option>
                        <option value="Medio Tiempo">Medio Tiempo</option>
                        <option value="Fines de Semana">Fines de Semana</option>
                        <option value="Por Definir">Por Definir</option>
                        <option value="custom">✍️ Personalizado...</option>
                      </select>

                      {![
                        "Lun-Vie (8am - 6pm)",
                        "Lun-Sáb (8am - 2pm)",
                        "Turno Rotativo",
                        "Turno Nocturno",
                        "Medio Tiempo",
                        "Fines de Semana",
                        "Por Definir",
                        ""
                      ].includes(vacanteForm.horario) && (
                        <motion.input 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          type="text" 
                          className="w-full bg-cyan-500/5 border border-cyan-500/20 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm"
                          value={vacanteForm.horario}
                          onChange={e => setVacanteForm({...vacanteForm, horario: e.target.value})}
                          placeholder="Escribe el horario personalizado..."
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">Estado de Vacante</label>
                    <select 
                      className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white font-bold outline-none focus:border-cyan-500/50 transition-all text-sm appearance-none"
                      value={vacanteForm.estado}
                      onChange={e => setVacanteForm({...vacanteForm, estado: e.target.value})}
                    >
                      <option value="abierta">Abierta</option>
                      <option value="cerrada">Cerrada</option>
                      <option value="pausada">Pausada</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/5 bg-white/[0.02] flex gap-4">
                {isEditing && (
                  <button 
                    onClick={() => handleDeleteVacante(editingRecordId)}
                    className="p-3 bg-red-500/10 text-red-500 border border-red-500/20 rounded-xl hover:bg-red-500 hover:text-white transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                <button 
                  onClick={handleSaveVacante}
                  className="flex-1 py-4 bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{isEditing ? 'Actualizar Vacante' : 'Publicar Vacante'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Postulaciones Sidebar */}
      <AnimatePresence>
        {isPostulacionesOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsPostulacionesOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              className="fixed top-0 right-0 h-full w-full max-w-[600px] bg-[#0F172A] border-l border-white/10 z-[101] flex flex-col"
            >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <ClipboardList className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">Candidatos</h2>
                    <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest">{selectedVacante?.puesto}</p>
                  </div>
                </div>
                <button onClick={() => setIsPostulacionesOpen(false)} className="p-2 hover:bg-white/5 rounded-lg text-slate-500">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                {postulaciones.filter(p => p.vacante_ref === selectedVacante?.puesto).length === 0 ? (
                  <div className="p-10 text-center text-slate-500 font-medium italic">Sin candidatos aún</div>
                ) : (
                  postulaciones.filter(p => p.vacante_ref === selectedVacante?.puesto).map((post, idx) => (
                    <div key={post._id || idx} className="bg-white/[0.02] border border-white/5 rounded-[1.5rem] p-5 space-y-4 relative group">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white border border-white/10 font-black">
                            {post.nombre?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">{post.nombre}</p>
                            <p className="text-[10px] text-slate-500">{post.telefono}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                           <span className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border ${
                             post.estado === 'nueva' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                             post.estado === 'entrevista' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                             post.estado === 'contratado' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                             'bg-slate-500/10 text-slate-400 border-slate-500/20'
                           }`}>
                             {post.estado}
                           </span>
                        </div>
                      </div>

                      <div className="space-y-2 bg-black/20 p-3 rounded-xl border border-white/5">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Experiencia / Notas</p>
                        <p className="text-xs text-slate-300 leading-relaxed italic">{post.experiencia || 'No proporcionada'}</p>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex gap-2">
                           <select 
                             className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1 text-[9px] font-bold text-slate-400 outline-none"
                             value={post.estado}
                             onChange={(e) => handleUpdatePostulacionStatus(post._id, e.target.value)}
                           >
                             <option value="nueva">Nueva</option>
                             <option value="en_revision">En Revisión</option>
                             <option value="entrevista">Entrevista</option>
                             <option value="contratado">Contratado</option>
                             <option value="rechazada">Rechazada</option>
                           </select>
                        </div>
                        <div className="flex items-center gap-2">
                          {post.estado !== 'entrevista' && (
                            <button 
                              onClick={() => handleAgendarEntrevista(post)}
                              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-[9px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center gap-2"
                            >
                              <Calendar className="w-3 h-3" />
                              <span>Agendar Entrevista</span>
                            </button>
                          )}
                          {post.estado === 'entrevista' && post.cita_entrevista_ref && (
                            <div className="flex items-center gap-1.5 text-amber-400">
                               <BadgeCheck className="w-3 h-3" />
                               <span className="text-[9px] font-black uppercase tracking-widest">{post.cita_entrevista_ref}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Scheduling Modal - Unificado */}
      <AnimatePresence>
        {isSchedulingOpen && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#020617]/90 backdrop-blur-xl z-[120] flex items-center justify-center p-4"
            onClick={() => setIsSchedulingOpen(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md bg-slate-900 border border-white/10 p-8 rounded-[2.5rem] shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center mb-8">
                 <div className="w-16 h-16 rounded-3xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 mb-4">
                    <Calendar className="w-8 h-8 text-indigo-400" />
                 </div>
                 <h2 className="text-2xl font-black text-white tracking-tighter uppercase italic">Programar Entrevista</h2>
                 <p className="text-slate-500 text-xs font-bold mt-1 uppercase tracking-widest">Pipeline de Talento</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-500 ml-1 tracking-widest">Fecha de la Cita</label>
                  <div className="relative group">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-indigo-400" />
                    <input 
                      type="date"
                      className="w-full bg-slate-950 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white font-black text-sm outline-none focus:border-indigo-500/50 transition-all"
                      value={schedulingData.fecha}
                      onChange={e => setSchedulingData({...schedulingData, fecha: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-500 ml-1 tracking-widest">Hora de Inicio</label>
                  <div className="relative group">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-indigo-400" />
                    <input 
                      type="time"
                      className="w-full bg-slate-950 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white font-black text-sm outline-none focus:border-indigo-500/50 transition-all"
                      value={schedulingData.hora}
                      onChange={e => setSchedulingData({...schedulingData, hora: e.target.value})}
                    />
                  </div>
                </div>

                <div className="pt-4 flex flex-col gap-3">
                  <button 
                    onClick={confirmScheduling}
                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-blue-700 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-xl hover:-translate-y-1 transition-all"
                  >
                    Confirmar y Notificar
                  </button>
                  <button 
                    onClick={() => setIsSchedulingOpen(false)}
                    className="w-full py-4 bg-white/5 text-slate-500 font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VacantesProPage;
